// Section 22: the persistent header (year and season, population, fragility with its factors,
// the steady-state counter with the last failed condition, End season with the reason it is
// disabled) and the End season confirm summary of what changed this season.
import {
  catalog,
  steadyTarget,
  validateDecision,
  type BuildCommand,
  type Config,
  type Decision,
  type Factor,
  type GameState,
  type JobId,
  type PersonId,
  type PolicyId,
  type PromptId,
  type Season,
  type SteadyCondition,
} from '../sim/api';
import { unansweredPrompts } from './events';
import { memo } from './memo';

/** Why End season is disabled; the UI turns the code into text. */
export type BlockReason =
  | { code: 'title' }
  | { code: 'reading-report' }
  | { code: 'resolving' }
  | { code: 'paused' }
  | { code: 'ended'; outcome: 'won' | 'lost' | null }
  | { code: 'unanswered'; count: number }
  | { code: 'invalid'; message: string };

export interface SteadyConditionRow {
  id: SteadyCondition;
  pass: boolean;
  value: number;
  threshold: number;
}

export interface HeaderVM {
  year: number;
  season: Season;
  turn: number;
  seed: string;
  population: number;
  /** Last season's births, deaths, arrivals and departures. */
  populationFactors: Factor[];
  fragility: number;
  fragilityFactors: Factor[];
  /**
   * M7: true when no season has been reported yet and the fragility shown is the projection's
   * forecast for the coming season (it read 0 on the first screen, whatever the settlement faced).
   */
  fragilityForecast: boolean;
  /**
   * P-03: true while no season has been reported and the first forecast is still being computed
   * (a few hundred ms after a new game): the header shows a pending mark, not a fragility of 0.
   */
  fragilityPending: boolean;
  steadyCounter: number;
  steadyTarget: number;
  /** The condition that failed at the last end-of-Winter check (Section 20). */
  lastFailed: SteadyCondition | null;
  /** The last check's conditions, for the counter's tooltip. */
  lastConditions: SteadyConditionRow[];
  canEndSeason: boolean;
  /** Why End season is disabled, if it is. */
  blocked: BlockReason | null;
  /** Open prompts the draft has not answered yet (Section 19, answered in phase 2). */
  unanswered: PromptId[];
  observer: boolean;
  ironman: boolean;
  outcome: 'won' | 'lost' | null;
  sandbox: boolean;
}

function populationFactors(p: {
  start: number;
  births: number;
  deaths: number;
  arrivals: number;
  departures: number;
}): Factor[] {
  const row = (id: string, value: number): Factor => ({
    source: `population:${id}`,
    op: id === 'start' ? 'base' : 'add',
    value,
    delta: value,
  });
  return [
    row('start', p.start),
    row('births', p.births),
    row('deaths', -p.deaths),
    row('arrivals', p.arrivals),
    row('departures', -p.departures),
  ].filter((f) => f.op === 'base' || f.value !== 0);
}

type StatusLike = 'title' | 'reporting' | 'deciding' | 'resolving' | 'paused' | 'ended';

function blockOf(
  game: GameState,
  draft: Decision | null,
  status: StatusLike,
  unanswered: PromptId[],
  cfg: Config | undefined,
): BlockReason | null {
  switch (status) {
    case 'title':
      return { code: 'title' };
    case 'reporting':
      return { code: 'reading-report' };
    case 'resolving':
      return { code: 'resolving' };
    case 'paused':
      return { code: 'paused' };
    case 'ended':
      return { code: 'ended', outcome: game.outcome?.kind ?? null };
    case 'deciding':
      break;
  }
  if (unanswered.length > 0) return { code: 'unanswered', count: unanswered.length };
  const problems = draft === null ? [] : validateDecision(game, draft, cfg);
  const first = problems[0];
  return first === undefined ? null : { code: 'invalid', message: first.message };
}

export const headerVM = memo(
  (
    game: GameState,
    draft: Decision | null,
    status: StatusLike,
    cfg?: Config,
    /** The projection's fragility for the coming season, if one is ready (first screen). */
    forecastFragility?: number | null,
  ): HeaderVM => {
    const last = game.history[game.history.length - 1];
    const forecastValue =
      last === undefined && forecastFragility !== undefined && forecastFragility !== null
        ? forecastFragility
        : null;
    const forecast = forecastValue !== null;
    // Section 22: End season waits for every open prompt (events, justice, pairings).
    const unanswered = unansweredPrompts(game, draft).map((p) => p.id);
    const blocked = blockOf(game, draft, status, unanswered, cfg);
    const result = game.steady.lastResult;
    return {
      year: game.year,
      season: game.season,
      turn: game.turn,
      seed: game.seed,
      population: Object.keys(game.people).length,
      populationFactors: last === undefined ? [] : populationFactors(last.population),
      fragility: last?.fragility ?? forecastValue ?? 0,
      fragilityFactors:
        last?.fragilityFactors ??
        (forecastValue === null
          ? []
          : [
              {
                source: 'fragility:forecast',
                op: 'base',
                value: forecastValue,
                delta: forecastValue,
                note: 'forecast for the coming season (no season reported yet)',
              },
            ]),
      fragilityForecast: forecast,
      fragilityPending: last === undefined && !forecast,
      steadyCounter: game.steady.counter,
      // Section 20 sandbox: the counter runs on toward twice the target after a win.
      steadyTarget: steadyTarget(game),
      lastFailed: game.steady.lastFailedCondition,
      lastConditions:
        result === null
          ? []
          : (Object.entries(result.conditions) as [SteadyCondition, SteadyConditionRow][]).map(
              ([id, c]) => ({ id, pass: c.pass, value: c.value, threshold: c.threshold }),
            ),
      canEndSeason: blocked === null,
      blocked,
      unanswered,
      observer: game.options.observer,
      ironman: game.options.ironman,
      outcome: game.outcome?.kind ?? null,
      sandbox: game.sandbox,
    };
  },
);

export const seasonsPerYear = (): number => catalog().seasonsPerYear;

// ---- End season confirm summary (Section 22 turn flow: "a confirm summary of what changed").

export interface DraftSummaryVM {
  assignments: { personId: PersonId; name: string; from: JobId; to: JobId }[];
  policies: { id: PolicyId; label: string; from: unknown; to: unknown }[];
  builds: { kind: BuildCommand['kind']; label: string }[];
  renames: { personId: PersonId; from: string; to: string }[];
  /** People and households pinned in the rations draft. */
  rationPins: number;
  /** Open prompts answered in this draft. */
  answers: number;
  toolOverrides: number;
  triageOverrides: number;
  /** Total number of changes; 0 means "nothing changed". */
  total: number;
}

export const draftSummaryVM = memo((game: GameState, draft: Decision | null): DraftSummaryVM => {
  const c = catalog();
  const d = draft;
  const nameOf = (id: PersonId): string => game.people[id]?.name ?? id;
  const assignments = Object.entries(d?.assignments ?? {})
    .map(([id, to]) => {
      const p = game.people[id as PersonId];
      return p === undefined || p.job === to
        ? null
        : { personId: p.id, name: p.name, from: p.job, to };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
  const policies = Object.entries(d?.policies ?? {}).map(([id, to]) => ({
    id: id as PolicyId,
    label: c.policies.find((p) => p.id === id)?.label ?? id,
    from: game.policies[id as PolicyId],
    to,
  }));
  const orderLabel = (orderId: string): string => {
    const o = game.buildQueue.find((b) => b.id === orderId);
    return o === undefined ? orderId : (c.buildings.find((b) => b.id === o.buildingType)?.label ?? o.buildingType);
  };
  const builds = (d?.builds ?? []).map((b) => ({
    kind: b.kind,
    label:
      b.kind === 'queue'
        ? (c.buildings.find((x) => x.id === b.buildingType)?.label ?? b.buildingType)
        : orderLabel(b.orderId),
  }));
  const renames = Object.entries(d?.renames ?? {}).map(([id, to]) => ({
    personId: id as PersonId,
    from: nameOf(id as PersonId),
    to,
  }));
  let rationPins = 0;
  for (const o of Object.values(d?.rations ?? {}))
    rationPins += Object.keys(o.people).length + Object.keys(o.households).length;
  const answers = Object.keys(d?.eventChoices ?? {}).length;
  const toolOverrides = Object.keys(d?.toolOverrides ?? {}).length;
  const triageOverrides = Object.keys(d?.triageOverrides ?? {}).length;
  return {
    assignments,
    policies,
    builds,
    renames,
    rationPins,
    answers,
    toolOverrides,
    triageOverrides,
    total:
      assignments.length +
      policies.length +
      builds.length +
      renames.length +
      rationPins +
      answers +
      toolOverrides +
      triageOverrides,
  };
});
